'use client';

import React, { Ref, useEffect, useState } from 'react';
import camelCase from 'lodash/fp/camelCase';
import flow from 'lodash/fp/flow';
import get from 'lodash/fp/get';
import groupBy from 'lodash/fp/groupBy';
import mapKeys from 'lodash/fp/mapKeys';

import {
	ShoppingCartItem,
	ShoppingCartItinerary,
	NoHotelProductsWarning,
	EventProductsOutsideDates,
} from '../index';
import { MODALS } from '@micro-apps/shared';
import { IconAdd } from '../../icon';
import { useTranslations } from '@fshr/context/i18n-context';
import { formatDateRange, formatPrice, getNumberOfGuests, defaultFetcher } from '@fshr/shared';
import LoadingIndicator from '../../loading-indicator';
import HorizontalLine from '../../horizontal-line';
import { Oj } from '@fshr/shared/types';
import { format, parseISO } from 'date-fns';
import { UpgradeRoomRecommendationCard } from '../UpgradeRoomRecommendation';
import { CartStore } from '@micro-apps/stores';
import Link from '../../link';
import { observer } from 'mobx-react-lite';
import { GlobalStore } from '@fshr/stores';
import { useGetRoomUpgradeAndAncilliariesRecos } from './useGetRoomUpgradeAndAncilliariesRecos';
import { AncilliariesRecommendationsComponent } from '../AncilliariesRecommendations/ancilliaries-recommendations';
import { getConfig } from '@micro-apps/env';
import BevSafeText from '../../bev-safe-text';

const DEFAULT_ITINERARY_DISCLAIMER =
	'Our concierge team will craft the best itinerary for you based on your request. Please note that your request is not confirmed until a concierge contacts you directly to finalize the details. Charges will only be applied once your request is confirmed.';

export interface BasketItem {
	onRequestEventProductType: string;
	productId: string;
}

interface RemoveFromCartResponse {
	success: boolean;
	message?: string;
}

interface ShoppingCartSummaryProps {
	officialName?: { __html: string };
	booking: Oj.Booking;
	basket?: BasketItem[];
	outsideStayDates?: BasketItem[];
	estimatedTotalDisclaimer?: string;
	removeFromCart: (params: { productId: string }) => Promise<RemoveFromCartResponse>;
	showPriceBreakdown: (params: {
		name: string;
		hotelProduct: Oj.hotelProduct;
		totalPrice: string;
	}) => void;
	removeBooking: () => void;
	insideCart?: boolean;
	FourSeasonsLogo?: React.ReactNode;
	noHotelProduct?: boolean;
	showNoHotelProductWarning?: boolean;
	accomPath?: string;
	thingsToDoPath?: string;
	onClose?: () => void;
	bookingId?: string;
	locale: string;
	cartStore: CartStore;
	globalStore: GlobalStore;
	outsideDatesComponentRef?: Ref<HTMLDivElement | null>;
	eventProductsOutsideDatesHighlightError?: boolean;
	isMpeJourney?: boolean;
	itineraryDisclaimer?: string;
}

export default observer(function ShoppingCartSummary(props: ShoppingCartSummaryProps) {
	const {
		officialName,
		booking,
		basket,
		outsideStayDates,
		removeFromCart,
		showPriceBreakdown,
		removeBooking,
		insideCart = false,
		FourSeasonsLogo,
		noHotelProduct = false,
		showNoHotelProductWarning = false,
		accomPath,
		thingsToDoPath,
		bookingId,
		locale,
		cartStore,
		globalStore,
		outsideDatesComponentRef,
		eventProductsOutsideDatesHighlightError = false,
		isMpeJourney = false,
		itineraryDisclaimer = DEFAULT_ITINERARY_DISCLAIMER,
	} = props;
	const t = useTranslations();
	const { NEXT_PUBLIC_BFF_BASE_URL } = getConfig();
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [mpeDetails, setMpeDetails] = useState<any>(null);
	const [isLoadingMpeDetails, setIsLoadingMpeDetails] = useState(false);
	const [mpeError, setMpeError] = useState<any>(null);
	// Fetch MPE details when it's an MPE journey
	useEffect(() => {
		if (isMpeJourney && !mpeDetails && !isLoadingMpeDetails && booking.hotelProducts) {
			const hotelProduct = booking.hotelProducts[0] as Oj.hotelProduct;
			const propertyOwsCode = hotelProduct?.hotelCode;
			// For MPE journeys, use the MPE rate code instead of the booking rate code
			const owsRateCode = 'FSHMPE';
			const startDate = hotelProduct?.checkInDate;

			if (propertyOwsCode && owsRateCode && startDate) {
				const fetchMpeDetails = async () => {
					try {
						setIsLoadingMpeDetails(true);
						setMpeError(null);
						const data = await defaultFetcher(`${NEXT_PUBLIC_BFF_BASE_URL}/journey/details`, {
							method: 'POST',
							headers: { 'Content-Type': 'application/json' },
							body: JSON.stringify({
								propertyOwsCode,
								owsRateCode,
								startDate,
							}),
						});
						setMpeDetails(data);
					} catch (error) {
						console.error('Failed to fetch MPE journey details:', error);
						setMpeError(error);
					} finally {
						setIsLoadingMpeDetails(false);
					}
				};
				fetchMpeDetails();
			}
		}
	}, [isMpeJourney, mpeDetails, isLoadingMpeDetails, booking]);

	const grouped = flow(groupBy('onRequestEventProductType'), mapKeys(camelCase))(basket || []);

	const departureTransportation =
		grouped['departureTransportation'] || ([] as unknown as Oj.EventProduct[]);
	const transportation = [
		...(grouped['transportation'] || []),
		...departureTransportation,
	] as unknown as Oj.EventProduct[];
	const arrivalAmenities = (grouped['arrivalAmenities'] || []) as unknown as Oj.EventProduct[];
	const itinerary = [
		...(grouped['spa'] || []),
		...(grouped['experiences'] || []),
		...(grouped['dining'] || []),
	] as unknown as Oj.EventProduct[];
	const handleRemoveItem = ({ productId }: { productId: string }) => {
		setIsSubmitting(true);
		Promise.resolve(removeFromCart({ productId })).finally(() => setIsSubmitting(false));
	};
	const cash = get(['price', 'total', 'cash'])(booking) || {};
	const totalPrice = formatPrice({
		value: Number(cash.amount) || 0,
		currencyCode: String(cash.currencyCode || 'USD'),
	});
	const hotelProduct = (booking?.hotelProducts?.[0] || {}) as Oj.hotelProduct;
	const { checkInDate, checkOutDate, price: hotelProductPrice } = hotelProduct;
	const formattedStayDateRange =
		checkInDate && checkOutDate ? formatDateRange(checkInDate, checkOutDate) : undefined;
	const taxesFeesCharges = (get(['prepay', 'taxesFeesCharges'])(hotelProductPrice) ||
		[]) as Oj.TaxesFeesCharges;

	const handleShowInfoModal = (hp: Oj.hotelProduct, priceStr: string) => {
		showPriceBreakdown({
			name: MODALS.BREAKDOWN,
			hotelProduct: hp,
			totalPrice: priceStr,
		});
	};
	const anonGuestRecosEnabled = Boolean(globalStore?.featureFlags?.enableAnonGuestRecommedation);
	const isAnonymousCart = !globalStore?.profile?.goldenId && anonGuestRecosEnabled;
	const { isLoading, data, error } = useGetRoomUpgradeAndAncilliariesRecos({
		bookingId,
		profile: globalStore.profile,
		locale,
		currencyCode: globalStore?.preferredCurrency,
		enableAnonGuestRecommedation: anonGuestRecosEnabled,
		globalStateFetching: globalStore.fetching,
	});

	// reco_impression only for signed-in guests; anon guest recos stay off Tealium impression pipeline
	const trackCartRecoImpressions = Boolean(globalStore.profile?.goldenId);
	const ancilliariesRecommendations = data?.ancilliariesRecommendations?.recommendations ?? [];
	const shouldShowAncilliariesRecommendations =
		!isAnonymousCart || ancilliariesRecommendations.length >= 3;

	return (
		<>
			{(isSubmitting || isLoadingMpeDetails) && <LoadingIndicator />}
			<div className="bg-white w-full h-full flex flex-col">
				{/* Main Content */}
				<div className="flex flex-col overflow-y-auto spacing-stack-l">
					<div className="padding-inline-l pt-spacing-card-padding-s flex flex-col spacing-stack-s">
						{FourSeasonsLogo}
						{officialName?.__html && !isMpeJourney && (
							<h3 className="text-h3" dangerouslySetInnerHTML={{ __html: officialName.__html }} />
						)}
						{isMpeJourney && mpeDetails && (
							<h3 className="text-h3">
								<BevSafeText>{mpeDetails.title}</BevSafeText>
							</h3>
						)}
						{formattedStayDateRange && <div className="text-label">{formattedStayDateRange}</div>}
					</div>
					<HorizontalLine additionalClasses="border-border-subtle" />
					{insideCart && noHotelProduct && showNoHotelProductWarning && (
						<div className="padding-inline-l">
							<NoHotelProductsWarning accomPath={accomPath || ''} />
						</div>
					)}
					{booking.hotelProducts && booking.hotelProducts.length > 0 && (
						<div className="padding-inline-l">
							<div className="grid spacing-grid-gutter">
								{booking.hotelProducts.map(hp => {
									if (isMpeJourney && mpeDetails) {
										// For MPE, show duration instead of rate plan title
										const cartDescription = hp.duration
											? `${hp.duration} Night${hp.duration > 1 ? 's' : ''}`
											: hp.roomRate?.ratePlanTitle;

										// For MPE, show total guests instead of adults & children breakdown
										const numberOfPeople = `${hp.guestCount?.numberAdults} Guest${hp.guestCount?.numberAdults !== 1 ? 's' : ''}`;

										return (
											<ShoppingCartItem
												key="mpe-property"
												name={mpeDetails.properties.map(property => property.name).join(', ')}
												cartDescription={cartDescription}
												roomRate={hp.roomRate as any}
												numberOfPeople={numberOfPeople}
												price={mpeDetails.basePrice}
												currencyCode={mpeDetails.currencyCode}
												taxAndFees={mpeDetails.feesDisclaimer}
												removeItem={removeBooking}
												insideCart={insideCart}
												productType={hp.productType}
												onRequestEventProductType={insideCart ? t('Stay') : ''}
												image={hp.roomTypes?.[0]?.image}
												isMpeJourney={isMpeJourney}
											/>
										);
									} else {
										// Regular hotel product rendering
										return hp.roomTypes?.map((rt, idx: number) => {
											return (
												<ShoppingCartItem
													key={rt.roomTypeId + idx}
													name={rt.description}
													cartDescription={hp.roomRate?.ratePlanTitle}
													roomRate={hp.roomRate as any}
													numberOfPeople={getNumberOfGuests({
														t,
														numberOfAdults: hp.guestCount?.numberAdults,
														numberOfChildren: hp.guestCount?.numberChildren,
													})}
													price={hp.price?.prepay?.base?.cash?.amount}
													currencyCode={hp.price?.total?.cash?.currencyCode}
													stayResortFees={hp.price?.prepay?.stayResortFees as any}
													taxAndFees={
														booking.resortFeesEnabled
															? (insideCart
																	? hp.roomRate?.feesDisclaimerPreCheckout
																	: hp.roomRate?.feesDisclaimerCheckout) ||
																hp.roomRate?.feesDisclaimer
															: hp.roomRate?.feesDisclaimer || undefined
													}
													removeItem={removeBooking}
													insideCart={insideCart}
													productType={hp.productType}
													image={rt.image}
													onRequestEventProductType={insideCart ? t('Stay') : ''}
												/>
											);
										});
									}
								})}
							</div>
						</div>
					)}
					{globalStore.profile || globalStore?.featureFlags?.enableAnonGuestRecommedation ? (
						<UpgradeRoomRecommendationCard
							error={error}
							isLoading={isLoading}
							roomUpgradeRecommendation={data?.roomUpgradeRecommendation}
							bookingId={bookingId}
							cartStore={cartStore}
							placement="shopping_cart-room_upgrade"
							trackRecoImpressions={trackCartRecoImpressions}
						/>
					) : null}
					{transportation.length > 0 && (
						<div className="padding-inline-l flex flex-col spacing-stack-m">
							<h4 className="text-h4">
								{t('Transportation')}
								{departureTransportation.length > 0 && '**'}
							</h4>
							<div className="grid spacing-grid-gutter">
								{transportation.map(item => (
									<ShoppingCartItem
										key={item.productId}
										name={
											item.name === 'Departure Drop-Off'
												? t('Departure Drop-Off')
												: t('Arrival Pick-up')
										}
										price={item.productTotal?.totalPrice?.amount}
										currencyCode={item.productTotal?.totalPrice?.currencyCode}
										removeItem={() =>
											handleRemoveItem({ productId: (item.productId || 0).toString() })
										}
										insideCart={insideCart}
										image={item.images?.[0]?.url}
									/>
								))}
							</div>
						</div>
					)}
					{arrivalAmenities.length > 0 && (
						<div className="padding-inline-l flex flex-col spacing-stack-m">
							<h4 className="text-h4">{t('In Your Room')}</h4>
							<div className="grid spacing-grid-gutter">
								{arrivalAmenities.map(item => {
									const requestDate = item.details?.fields?.find(f => f.name === 'Request_Date');
									const formattedRequestDate = requestDate
										? format(parseISO(requestDate.value), 'MMM d')
										: undefined;
									return (
										<ShoppingCartItem
											key={item.productId}
											name={item.name}
											price={item.productTotal?.totalPrice?.amount}
											currencyCode={item.productTotal?.totalPrice?.currencyCode}
											quantity={item.ticketOptionType?.totalCount}
											removeItem={() =>
												handleRemoveItem({ productId: (item.productId || 0).toString() })
											}
											insideCart={insideCart}
											image={item.images?.[0]?.url}
											requestDate={formattedRequestDate}
										/>
									);
								})}
							</div>
						</div>
					)}
					{/* Ancillary Requests Section */}
					{!isMpeJourney && (
						<section className="padding-inline-l flex flex-col spacing-stack-m">
							<div className="text-h4">{t('Itinerary Requests')}</div>
							{itinerary.length > 0 && (
								<ShoppingCartItinerary
									itinerary={itinerary}
									removeItem={handleRemoveItem}
									insideCart={insideCart}
									handleShowInfoModal={handleShowInfoModal}
								/>
							)}

							{/* Add Ancillary Requests Discovery Box (Things to Do) */}
							{insideCart && (
								<>
									{outsideStayDates && (
										<>
											<div tabIndex={-1} ref={outsideDatesComponentRef}></div>
											<EventProductsOutsideDates
												eventProductsOutsideDates={outsideStayDates}
												formattedStayDateRange={formattedStayDateRange}
												removeItem={handleRemoveItem}
												handleShowInfoModal={handleShowInfoModal}
												highlightError={eventProductsOutsideDatesHighlightError}
											/>
										</>
									)}
									{thingsToDoPath && (
										<Link
											className="spacing-stack-m spacing-card-padding-xs border border-dashed border-border-default rounded-[5px] cursor-pointer bg-surface hover:bg-surface-hover flex flex-row items-center justify-start spacing-inline-m"
											href={thingsToDoPath}
											intent="unstyled"
										>
											<IconAdd />
											<div className="flex flex-col">
												<span className="text-label">{t('Add Itinerary Requests')}</span>
												<span className="text-caption">
													{t('Request Experience, Dining and Spa')}
												</span>
											</div>
										</Link>
									)}
								</>
							)}
							{itinerary.length > 0 && (
								<p className="text-caption">
									*<BevSafeText>{itineraryDisclaimer}</BevSafeText>
								</p>
							)}
						</section>
					)}

					{!isMpeJourney && shouldShowAncilliariesRecommendations && (
						<AncilliariesRecommendationsComponent
							isLoading={isLoading}
							title={isAnonymousCart ? 'Top Picks for Your Stay' : undefined}
							minRecommendationsToShow={isAnonymousCart ? 3 : 1}
							maxRecommendationsToShow={isAnonymousCart ? 3 : 5}
							showHighlightedRecommendation={!isAnonymousCart}
							ancilliariesRecommendations={ancilliariesRecommendations}
							highlightedRecommendation={
								isAnonymousCart ? undefined : data?.highlightedRecommendation?.recommendation
							}
							trackRecoImpressions={trackCartRecoImpressions}
						/>
					)}

					{/* Taxes & Fees section */}
					{!insideCart && taxesFeesCharges && taxesFeesCharges.length > 0 && (
						<div className="padding-inline-l flex flex-col spacing-stack-m">
							<div className="text-h4">{t('Taxes and Fees')}</div>
							{taxesFeesCharges.map(({ code, description, cash }) => (
								<div key={code} className="flex flex-row justify-between items-center">
									<span className="text-subtitle2">
										<BevSafeText>{description}</BevSafeText>
									</span>
									<span className="text-subtitle2 text-right">
										{formatPrice({ value: cash?.amount || 0, currencyCode: cash?.currencyCode })}
									</span>
								</div>
							))}
						</div>
					)}
					{insideCart && (
						<div className="padding-inline-l flex flex-row justify-between items-center">
							<span className="text-h4">{t('Taxes & Fees')}</span>
							<span className="text-label text-right text-[#666666]">
								{t('To be calculated at checkout')}
							</span>
						</div>
					)}

					<HorizontalLine additionalClasses="border-border-subtle" />
					<div className="padding-inline-l flex flex-col spacing-stack-s">
						<div className="flex justify-between gap-100">
							<span className="text-h4">{t('Est. Total')}</span>
							<span className="text-subtitle1">
								{isMpeJourney
									? formatPrice({
											value: mpeDetails?.estimatedTotal,
											currencyCode: mpeDetails?.currencyCode,
										})
									: String(totalPrice)}
							</span>
						</div>
					</div>
				</div>
			</div>
		</>
	);
});
